import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretLeft, CaretRight, MapPin, Stethoscope } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import ProviderCard from "../components/ProviderCard";
import { api } from "../lib/api";
import { richItemListSchema } from "../lib/jsonLd";

const US_STATES = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "FL": "Florida", "GA": "Georgia",
  "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois", "IN": "Indiana", "IA": "Iowa",
  "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi", "MO": "Missouri",
  "MT": "Montana", "NE": "Nebraska", "NV": "Nevada", "NH": "New Hampshire", "NJ": "New Jersey",
  "NM": "New Mexico", "NY": "New York", "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio",
  "OK": "Oklahoma", "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont",
  "VA": "Virginia", "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming",
  "DC": "District of Columbia"
};

const SPECIALTY_DISPLAY_NAMES = {
  "cardiology": "Cardiology",
  "cardiologist": "Cardiology",
  "dermatology": "Dermatology", 
  "dermatologist": "Dermatology",
  "pediatrics": "Pediatrics",
  "pediatrician": "Pediatrics",
  "psychiatry": "Psychiatry",
  "psychiatrist": "Psychiatry",
  "orthopedics": "Orthopedics",
  "orthopedic": "Orthopedics",
  "family-medicine": "Family Medicine",
  "internal-medicine": "Internal Medicine",
  "neurology": "Neurology",
  "neurologist": "Neurology",
  "oncology": "Oncology",
  "oncologist": "Oncology",
  "radiology": "Radiology"
};

const ITEMS_PER_PAGE = 20;

const SpecialtyStateDirectory = () => {
  const { specialty, stateCode } = useParams();
  const [searchParams] = useSearchParams();
  
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  
  const stateUpper = stateCode.toUpperCase();
  const stateName = US_STATES[stateUpper] || stateCode;
  const specialtyName = SPECIALTY_DISPLAY_NAMES[specialty.toLowerCase()] || specialty.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const fetchResults = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        state: stateUpper,
        limit: ITEMS_PER_PAGE,
        skip: (currentPage - 1) * ITEMS_PER_PAGE
      };
      
      const data = await api.getProvidersBySpecialty(specialty, params);
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error("Error fetching providers:", error);
      setResults([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [specialty, stateUpper, currentPage]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const pageTitle = specialtyName + " NPI Lookup in " + stateName + " — Find " + specialtyName + " Doctors in " + stateUpper;
  const pageDescription = specialtyName + " NPI lookup for " + stateName + ". Search " + totalCount.toLocaleString() + "+ " + specialtyName.toLowerCase() + " providers by NPI number, verify NPPES credentials, and find specialist physicians in " + stateUpper + ".";

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={"https://npilookup.com/specialty/" + specialty + "/" + stateCode.toLowerCase()} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "MedicalWebPage",
          "name": specialtyName + " in " + stateName,
          "description": pageDescription,
          "url": "https://npilookup.com/specialty/" + specialty + "/" + stateCode.toLowerCase(),
          "specialty": { "@type": "MedicalSpecialty", "name": specialtyName }
        })}</script>
        <script type="application/ld+json">{JSON.stringify(richItemListSchema(
          `${specialtyName} Providers in ${stateName}`,
          `Verified ${specialtyName} physicians in ${stateName} with NPI numbers from the NPPES national registry.`,
          results,
          `/specialty/${specialty}/${stateCode.toLowerCase()}`
        ))}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://npilookup.com/" },
            { "@type": "ListItem", "position": 2, "name": specialtyName, "item": "https://npilookup.com/specialty/" + specialty },
            { "@type": "ListItem", "position": 3, "name": stateName }
          ]
        })}</script>
      </Helmet>

      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to={"/specialty/" + specialty} className="hover:text-teal">{specialtyName}</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">{stateName}</li>
            </ol>
          </nav>

          {/* Header */}
          <div className="bg-white rounded-lg border border-slate-200 p-6 sm:p-8 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-teal/10 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-teal" />
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-slate-400" />
                <span className="text-slate-500 font-medium">{stateName}</span>
              </div>
            </div>
            
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-navy mb-2" data-testid="specialty-state-title">
              {specialtyName} in {stateName}
            </h1>
            <p className="text-slate-500 text-lg">
              Find {specialtyName.toLowerCase()} healthcare providers in {stateName}
            </p>
            
            <div className="mt-4 flex items-center gap-4">
              <span className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full" data-testid="specialty-state-count">
                {loading ? "Loading..." : totalCount.toLocaleString() + " providers found"}
              </span>
            </div>
          </div>

          {/* Related Links */}
          <div className="flex flex-wrap gap-2 mb-6">
            <Link
              to={"/specialty/" + specialty}
              className="text-sm px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-teal text-slate-600 hover:text-teal transition-colors"
            >
              All {specialtyName} Providers
            </Link>
            <Link
              to={"/state/" + stateCode}
              className="text-sm px-3 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-teal text-slate-600 hover:text-teal transition-colors"
            >
              All Providers in {stateName}
            </Link>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-200 p-6">
                  <div className="skeleton h-4 w-20 mb-3 rounded" />
                  <div className="skeleton h-6 w-3/4 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/2 mb-2 rounded" />
                  <div className="skeleton h-4 w-1/3 mb-4 rounded" />
                  <div className="skeleton h-10 w-full rounded" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">
                No providers found
              </h3>
              <p className="text-slate-500 mb-6">
                We couldn't find any {specialtyName.toLowerCase()} providers in {stateName}.
              </p>
              <Link to={"/specialty/" + specialty}>
                <Button variant="outline">
                  View All {specialtyName} Providers
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div 
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                data-testid="specialty-state-results-grid"
              >
                {results.map((provider) => (
                  <ProviderCard key={provider.number} provider={provider} />
                ))}
              </div>

              {/* Pagination */}
              {(hasMore || currentPage > 1) && (
                <div className="flex items-center justify-center gap-3 mt-8" data-testid="specialty-state-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <CaretLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  
                  <span className="text-sm text-slate-500 font-medium px-3">
                    Page {currentPage}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={!hasMore}
                    className="pagination-btn"
                  >
                    Next
                    <CaretRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
          
          {/* SEO Content */}
          <div className="mt-12 bg-white rounded-lg border border-slate-200 p-6">
            <h2 className="font-display font-semibold text-xl text-slate-900 mb-4">
              About {specialtyName} in {stateName}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Find {specialtyName.toLowerCase()} healthcare providers in {stateName}. 
              Our directory includes {totalCount.toLocaleString()}+ registered {specialtyName.toLowerCase()} professionals 
              with verified NPI numbers. Each provider listing includes credentials, contact information, 
              practice addresses, and specialty certifications. Use the NPI Lookup to verify provider 
              credentials and find {specialtyName.toLowerCase()} doctors near you in {stateName}.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpecialtyStateDirectory;
